import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiSave, FiPlus, FiTrash2 } from "react-icons/fi";
import { getRatingSettings, updateRatingSettings, approveConfiguration } from "../../services/settingsApi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Button, Loader, ErrorState, Banner, DataTable } from "../../components/common";
import RatingScale from "../../components/performance/RatingScale";
import PipRuleCard from "./components/PipRuleCard"; // [NEW] PM-9: the only addition to the original page
import { calcFinalScore, getRating } from "../../utils/scoreCalculator";
import { formatDateTime, formatScore } from "../../utils/format";
import { getErrorMessage } from "../../utils/errors";

const api = { getRatingSettings, updateRatingSettings, approveConfiguration };

const SAMPLE = { goalScore: 85, kraScore: 90, kpiScore: 75 };

export default function RatingSettingsPage() {
  const { data, error, reload } = useAsync(() => api.getRatingSettings(), []);
  const [weights, setWeights] = useState({ goals: 50, kra: 50, kpi: 0 });
  const [bands, setBands] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      // [UPDATED] KPI is not used for scoring: it is not shown and is always saved as 0 (Goals + KRA = 100%).
      setWeights({ ...data.weights, kpi: 0 });
      setBands(data.bands.map((b) => ({ ...b })));
    }
  }, [data]);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const total = Number(weights.goals || 0) + Number(weights.kra || 0) + Number(weights.kpi || 0);
  const sampleScore = calcFinalScore(SAMPLE, { goals: Number(weights.goals), kra: Number(weights.kra), kpi: Number(weights.kpi) });
  const sampleRating = getRating(sampleScore, bands.map((b) => ({ ...b, min: Number(b.min), max: Number(b.max) })));

  const setBand = (i, key) => (e) => setBands((list) => list.map((b, idx) => (idx === i ? { ...b, [key]: e.target.value } : b)));

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.updateRatingSettings({ weights, bands });
      toast.success(saved.approved
        ? "New scoring version saved and approved. Cycles created from now on can use it."
        : "New scoring version saved as a draft. Someone with approval rights must approve it.");
      reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Scoring & ratings"
        subtitle={data.updatedAt ? `Last changed by ${data.updatedByName} on ${formatDateTime(data.updatedAt)}.` : "No scoring policy yet. Save to create the first version."}
        actions={!data.readOnly && <Button icon={FiSave} onClick={save} loading={saving} disabled={total !== 100}>Save as new version</Button>}
      />
      {data.readOnly ? (
        <Banner tone="warning" title="Read-only">You don't have permission to view or change the scoring configuration. The defaults are shown.</Banner>
      ) : (
        <Banner tone="info" title="Settings are versioned">
          Saving creates a new version of the scoring policy and rating scheme. Each cycle keeps the version chosen when it
          was created, so running cycles and finalized results are not affected.
        </Banner>
      )}

      <div className="grid-2">
        <Card title="Component weights" subtitle="Goals and KRA must add up to 100%.">
          <div className="weight-inputs">
            {[["goals", "Goals"], ["kra", "KRA"]].map(([key, label]) => (
              <label key={key} className="weight-input">
                <span>{label}</span>
                <span className="input-suffix">
                  <input type="number" min="0" max="100" className="form-control" value={weights[key]} onChange={(e) => setWeights({ ...weights, [key]: e.target.value })} />
                  <em>%</em>
                </span>
              </label>
            ))}
          </div>
          <p className={`weight-total ${total === 100 ? "is-ok" : "is-bad"}`} role="status">
            Total {total}%{total !== 100 ? `. Adjust by ${Math.round((100 - total) * 100) / 100}% to save.` : ""}
          </p>
        </Card>
        <Card title="Worked example">
          <p className="muted">An employee with goal score {SAMPLE.goalScore}, KRA score {SAMPLE.kraScore} and KPI score {SAMPLE.kpiScore}:</p>
          <p className="example-calc">
            ({SAMPLE.goalScore} × {weights.goals}%) + ({SAMPLE.kraScore} × {weights.kra}%) + ({SAMPLE.kpiScore} × {weights.kpi}%) = <strong>{formatScore(sampleScore, 2)}</strong>
          </p>
          <p>Rating: <strong>{sampleRating.label}</strong></p>
        </Card>
      </div>

      <Card
        title="Rating bands"
        subtitle="Set where each band starts. Each band ends just below the next band's start (worked out for you), and the top band covers everything above. Colours follow the band order."
        actions={<Button variant="ghost" size="sm" icon={FiPlus} onClick={() => setBands([...bands, { label: "New rating", min: 0, max: 0, tone: "neutral" }])}>Add band</Button>}
      >
        <div className="band-table">
          <div className="band-row band-head">
            <span>Rating name</span><span>From</span><span>To (automatic)</span><span>Colour (automatic)</span><span />
          </div>
          {bands.map((b, i) => {
            const higher = bands.map((x) => Number(x.min)).filter((m) => m > Number(b.min)).sort((x, y) => x - y)[0];
            const to = higher === undefined ? "and above" : formatScore(Math.round((higher - 0.01) * 100) / 100, 2);
            return (
            <div key={i} className="band-row">
              <input className="form-control" value={b.label} onChange={setBand(i, "label")} aria-label="Rating name" />
              <input className="form-control" type="number" step="0.01" value={b.min} onChange={setBand(i, "min")} aria-label="From score" />
              <span className="form-control is-readonly" aria-label="To score (automatic)">{to}</span>
              <span className={`badge badge-${b.tone}`} title="Colours are assigned by band order"><span className="badge-dot" />{b.tone}</span>
              <Button variant="ghost" size="sm" icon={FiTrash2} aria-label={`Remove ${b.label}`} disabled={bands.length <= 2} onClick={() => setBands(bands.filter((_, idx) => idx !== i))} />
            </div>
            );
          })}
        </div>
        <p className="footnote">When an employee has no performance data, the system shows "Not Yet Rated" instead of the lowest band.</p>
        <h3 className="section-title">Preview</h3>
        <RatingScale
          score={sampleScore}
          bands={bands.map((b) => {
            const higher = bands.map((x) => Number(x.min)).filter((m) => m > Number(b.min)).sort((x, y) => x - y)[0];
            return { ...b, min: Number(b.min), max: higher === undefined ? Math.max(100, Number(b.min)) : Math.round((higher - 0.01) * 100) / 100 };
          })}
        />
      </Card>

      <PipRuleCard data={data} onSaved={reload} />

      {!data.readOnly && (
        <Card title="Versions" subtitle="Drafts must be approved before a cycle can launch with them." padded={false}>
          <DataTable keepOrder
            dense
            rowKey={(r) => `${r.kind}-${r.id}`}
            rows={[...data.policies, ...data.schemes]}
            emptyTitle="No versions yet"
            columns={[
              { key: "name", label: "Version", render: (r) => <span className="cell-stack"><strong>{r.name}</strong><small>{r.kind === "scoring_policy" ? "Scoring policy" : "Rating scheme"}</small></span> },
              { key: "detail", label: "Content" },
              { key: "created", label: "Created", render: (r) => <span className="cell-stack"><span>{formatDateTime(r.createdAt)}</span><small>{r.createdBy}</small></span> },
              { key: "status", label: "Status", render: (r) => <span className={`badge badge-${r.status === "APPROVED" ? "success" : "warning"}`}><span className="badge-dot" />{r.status === "APPROVED" ? "Approved" : "Draft"}</span> },
              {
                key: "act", label: "", align: "right",
                render: (r) => r.status === "DRAFT" && (
                  <Button size="sm" variant="secondary" onClick={async () => {
                    try {
                      await api.approveConfiguration(r.kind, r.id, "Approved from Scoring & ratings");
                      toast.success(`${r.name} approved.`);
                      reload();
                    } catch (err) {
                      toast.error(getErrorMessage(err));
                    }
                  }}>Approve</Button>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}